import React, { useState, useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import moment from 'moment-timezone'
import {
    Button,
    Card,
    Icon,
    Input,
    Heading,
    Paragraph,
    LoadingFull as Loading
} from '@arnsbogroup/react-voicearchive'
import NiceModal from '@ebay/nice-modal-react'
import SchedulingPollFinalizeModal from '../SchedulingPollFinalizeModal'
import { FormattedDate, FormattedTime, FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'

const SchedulingPollDetailPage = () => {
    const { formatMessage } = useIntl()
    const history = useHistory()
    const { id } = useParams()
    const [request, setRequest] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [shareableLink, setShareableLink] = useState(null)
    const [expandedSlots, setExpandedSlots] = useState({})

    useEffect(() => {
        loadRequest()
    }, [id])

    const loadRequest = async () => {
        setLoading(true)
        try {
            const response = await fetch(`/rest/v2/schedulingpoll/${id}`, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            })

            const data = await response.json()

            if (data.success && data.data) {
                const link = data.data.publicUrl || `${window.location.origin}/scheduling-poll/${data.data.hash_id}`
                setRequest(data.data)
                setShareableLink(link)
                setLoading(false)
            } else {
                throw new Error(data.errors ? data.errors.join(', ') : 'Failed to load poll')
            }
        } catch (error) {
            setError(error.message)
            setLoading(false)
            toastr.error(formatMessage(messages.loadPollError))
        }
    }

    const copyLinkToClipboard = () => {
        if (shareableLink) {
            navigator.clipboard.writeText(shareableLink).then(() => {
                toastr.success(formatMessage(messages.linkCopied))
            }).catch(() => {
                toastr.error(formatMessage(messages.linkCopyFailed))
            })
        }
    }

    const getVoteColor = (available, unavailable, maybe, total) => {
        if (total === 0) return 'bg-gray-100'

        const availablePercent = (available / total) * 100

        if (availablePercent >= 80) return 'bg-green-500 text-white'
        if (availablePercent >= 60) return 'bg-green-400'
        if (availablePercent >= 40) return 'bg-yellow-300'
        if (availablePercent >= 20) return 'bg-orange-300'
        return 'bg-red-300'
    }

    const getBestTimeSlot = () => {
        if (!request || !request.timeSlots) return null

        let bestSlot = null
        let maxAvailable = -1

        request.timeSlots.forEach(slot => {
            const available = slot.voteCounts?.available || 0
            if (available > maxAvailable) {
                maxAvailable = available
                bestSlot = slot
            }
        })

        return bestSlot
    }

    const handleDuplicate = () => {
        history.push('/scheduling-polls/create', {
            duplicateFrom: {
                title: `Copy of ${request.title}`,
                description: request.description || '',
                includeBranding: !!request.include_branding,
                timeSlots: (request.timeSlots || []).map(slot => ({
                    uid: null,
                    startDatetime: moment.utc(slot.start_datetime).local().format('YYYY-MM-DDTHH:mm'),
                    endDatetime: moment.utc(slot.end_datetime).local().format('YYYY-MM-DDTHH:mm'),
                    isExisting: false,
                })),
            },
        })
    }

    const handleFinalize = async () => {
        const bestSlot = getBestTimeSlot()

        await NiceModal.show(SchedulingPollFinalizeModal, {
            request,
            bestTimeSlot: bestSlot
        })

        loadRequest()
    }

    const handleClosePoll = async () => {
        if (!window.confirm(formatMessage(messages.closePollConfirm))) {
            return
        }

        try {
            const response = await fetch(`/rest/v2/schedulingpoll/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    closePoll: true
                })
            })

            const data = await response.json()

            if (data.success) {
                toastr.success(formatMessage(messages.pollClosedSuccess))
                loadRequest()
            } else {
                toastr.error(data.errors ? data.errors.join(', ') : formatMessage(messages.closePollError))
            }
        } catch (error) {
            toastr.error(error.message || formatMessage(messages.closePollError))
        }
    }

    const toggleSlotExpansion = (slotUid) => {
        setExpandedSlots(prev => ({
            ...prev,
            [slotUid]: !prev[slotUid]
        }))
    }

    const getParticipantVotesForSlot = (slotUid) => {
        if (!request.responses) return []

        const participantVotes = []

        request.responses.forEach(response => {
            if (response.votes && typeof response.votes === 'object') {
                const vote = response.votes[slotUid]
                if (vote) {
                    participantVotes.push({
                        name: response.participant_name,
                        email: response.participant_email,
                        vote: vote
                    })
                }
            }
        })

        return participantVotes
    }

    const getVoteBadgeClass = (vote) => {
        if (vote === 'available') {
            return 'bg-green-100 text-green-800 border-green-300'
        } else if (vote === 'maybe') {
            return 'bg-yellow-100 text-yellow-800 border-yellow-300'
        } else {
            return 'bg-red-100 text-red-800 border-red-300'
        }
    }

    if (loading) {
        return <Loading />
    }

    if (error || !request) {
        return (
            <div className="container mx-auto px-4 py-8 pb-16">
                <Card className="bg-red-50 border border-red-200 p-4">
                    <Paragraph className="text-negative mb-0">
                        <FormattedMessage {...messages.error} values={{ error: error || formatMessage(messages.requestNotFound) }} />
                    </Paragraph>
                </Card>
            </div>
        )
    }

    const bestSlot = getBestTimeSlot()
    const userTimezone = moment.tz.guess()

    return (
        <div className="w-full flex-1 px-4 py-4">
            <div className="mb-4">
                <div className="flex justify-between items-start">
                    <div className="flex-1 pr-4">
                        <Heading level={1} className="text-2xl font-bold mb-2">{request.title}</Heading>
                        {request.description && (
                            <Paragraph className="text-sm text-gray-600 mb-0">{request.description}</Paragraph>
                        )}
                    </div>
                    <div className="text-right">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${
                            request.status === 'open' ? 'bg-primary text-white' :
                            request.status === 'closed' ? 'bg-negative text-white' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                    </div>
                </div>
            </div>

            <Card className="bg-blue-50 border border-blue-200 p-3 mb-4">
                <Paragraph className="text-xs font-medium text-gray-700 mb-2">
                    <FormattedMessage {...messages.participantLink} />
                </Paragraph>
                <div className="flex gap-2">
                    <Input
                        value={shareableLink}
                        readOnly
                        className="text-sm"
                    />
                    <Button
                        onClick={copyLinkToClipboard}
                        size="sm"
                    >
                        <FormattedMessage {...messages.copy} />
                    </Button>
                </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-4">
                <Card className="p-3">
                    <Paragraph className="text-xs text-gray-600 mb-1">
                        <FormattedMessage {...messages.timeSlots} />
                    </Paragraph>
                    <Paragraph className="text-xl font-bold text-gray-900 mb-0">{request.timeSlots?.length || 0}</Paragraph>
                </Card>
                <Card className="p-3">
                    <Paragraph className="text-xs text-gray-600 mb-1">
                        <FormattedMessage {...messages.responses} />
                    </Paragraph>
                    <Paragraph className="text-xl font-bold text-primary mb-0">{request.responseCount || 0}</Paragraph>
                </Card>
                <Card className="p-3">
                    <Paragraph className="text-xs text-gray-600 mb-1">
                        <FormattedMessage {...messages.created} />
                    </Paragraph>
                    <Paragraph className="text-sm font-medium text-gray-900 mb-0">
                        <FormattedDate value={new Date(request.crdate * 1000)} />
                    </Paragraph>
                </Card>
            </div>

            <Card className="mb-4">
                <div className="px-4 py-3 border-b border-gray-200">
                    <Heading level={2} className="text-lg font-bold mb-0">
                        <FormattedMessage {...messages.availabilityOverview} />
                    </Heading>
                    <Paragraph className="text-xs text-gray-600 mt-1 mb-0">
                        <FormattedMessage {...messages.timesShownIn} values={{ timezone: userTimezone }} />
                    </Paragraph>
                </div>
                <div className="p-4">
                    {request.timeSlots && request.timeSlots.length > 0 ? (
                        <div className="space-y-2">
                            {[...request.timeSlots]
                                .sort((a, b) => moment.utc(a.start_datetime).valueOf() - moment.utc(b.start_datetime).valueOf())
                                .map((slot) => {
                                    const counts = slot.voteCounts || { available: 0, unavailable: 0, maybe: 0 }
                                    const total = counts.available + counts.unavailable + counts.maybe
                                    const bgColor = getVoteColor(counts.available, counts.unavailable, counts.maybe, total)
                                    const isBest = bestSlot && bestSlot.uid === slot.uid

                                    const isExpanded = expandedSlots[slot.uid]
                                    const participantVotes = getParticipantVotesForSlot(slot.uid)

                                    return (
                                        <div
                                            key={slot.uid}
                                            className={`border rounded-lg ${isBest ? 'border-green-500 border-2' : 'border-gray-200'}`}
                                        >
                                            <div
                                                className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                                                onClick={() => toggleSlotExpansion(slot.uid)}
                                            >
                                            <div className="flex justify-between items-center gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <Icon
                                                            icon={['fas', isExpanded ? 'chevron-down' : 'chevron-right']}
                                                            className="text-gray-400 text-xs flex-shrink-0"
                                                        />
                                                        <div className="font-medium text-sm text-gray-900">
                                                            <FormattedDate
                                                                value={moment.utc(slot.start_datetime).tz(userTimezone).toDate()}
                                                                weekday="short"
                                                                month="short"
                                                                day="numeric"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-gray-600 ml-5">
                                                        <FormattedTime value={moment.utc(slot.start_datetime).tz(userTimezone).toDate()} /> - <FormattedTime value={moment.utc(slot.end_datetime).tz(userTimezone).toDate()} />
                                                    </div>
                                                    {isBest && total > 0 && (
                                                        <span className="inline-block mt-1 ml-5 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">
                                                            <FormattedMessage {...messages.best} />
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 flex-shrink-0">
                                                    <div className="text-center">
                                                        <div className={`px-2 py-1 rounded ${bgColor}`}>
                                                            <div className="font-bold text-sm">{counts.available}</div>
                                                            <div className="text-xs"><FormattedMessage {...messages.available} /></div>
                                                        </div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="px-2 py-1 rounded bg-yellow-100">
                                                            <div className="font-bold text-sm">{counts.maybe}</div>
                                                            <div className="text-xs"><FormattedMessage {...messages.maybe} /></div>
                                                        </div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="px-2 py-1 rounded bg-red-100">
                                                            <div className="font-bold text-sm">{counts.unavailable}</div>
                                                            <div className="text-xs"><FormattedMessage {...messages.unavailable} /></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="border-t border-gray-200 bg-gray-50 p-3">
                                                <Heading level={4} className="text-xs font-semibold text-gray-700 mb-2">
                                                    <FormattedMessage {...messages.participantResponses} values={{ count: participantVotes.length }} />
                                                </Heading>
                                                {participantVotes.length > 0 ? (
                                                    <div className="space-y-1.5">
                                                        {participantVotes.map((pv, idx) => (
                                                            <div
                                                                key={idx}
                                                                className="flex justify-between items-center p-2 bg-white rounded border border-gray-200"
                                                            >
                                                                <div className="flex-1 min-w-0 pr-2">
                                                                    <div className="font-medium text-xs text-gray-900 truncate">
                                                                        {pv.name}
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 truncate">
                                                                        {pv.email}
                                                                    </div>
                                                                </div>
                                                                <span className={`px-2 py-1 rounded text-xs font-medium border ${getVoteBadgeClass(pv.vote)} flex-shrink-0`}>
                                                                    <Icon
                                                                        icon={['fas', pv.vote === 'available' ? 'check' : pv.vote === 'maybe' ? 'question' : 'times']}
                                                                        className="mr-1"
                                                                    />
                                                                    {pv.vote.charAt(0).toUpperCase() + pv.vote.slice(1)}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <Paragraph className="text-sm text-gray-500 italic mb-0">
                                                        <FormattedMessage {...messages.noResponses} />
                                                    </Paragraph>
                                                )}
                                            </div>
                                        )}
                                        </div>
                                    )
                                })}
                        </div>
                    ) : (
                        <Paragraph className="text-gray-500 mb-0">
                            <FormattedMessage {...messages.noTimeSlots} />
                        </Paragraph>
                    )}
                </div>
            </Card>

            {request.responses && request.responses.length > 0 && (
                <Card className="mb-4">
                    <div className="px-4 py-3 border-b border-gray-200">
                        <Heading level={2} className="text-lg font-bold mb-0">
                            <FormattedMessage {...messages.participantResponsesTable} values={{ count: request.responses.length }} />
                        </Heading>
                    </div>
                    <div className="p-4">
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-700">
                                            <FormattedMessage {...messages.name} />
                                        </th>
                                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-700">
                                            <FormattedMessage {...messages.email} />
                                        </th>
                                        <th className="text-left py-2 px-2 text-xs font-medium text-gray-700">
                                            <FormattedMessage {...messages.submitted} />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {request.responses.map((response, idx) => (
                                        <tr key={idx} className="border-b last:border-b-0">
                                            <td className="py-2 px-2 text-xs">{response.participant_name}</td>
                                            <td className="py-2 px-2 text-xs text-gray-600">{response.participant_email}</td>
                                            <td className="py-2 px-2 text-xs text-gray-600">
                                                <FormattedDate value={new Date(response.submitted_at * 1000)} /> <FormattedTime value={new Date(response.submitted_at * 1000)} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Card>
            )}

            <div className="flex justify-end gap-2 mt-2">
                <Button
                    onClick={handleDuplicate}
                    size="sm"
                    variant="outline"
                >
                    <Icon icon={['fas', 'copy']} className="mr-1" />
                    <FormattedMessage {...messages.duplicatePoll} />
                </Button>

                {request.status === 'open' && (
                    <>
                        <Button
                            onClick={() => history.push(`/scheduling-polls/${id}/edit`)}
                            size="sm"
                            variant="outline"
                        >
                            <Icon icon={['fas', 'pencil-alt']} className="mr-1" />
                            <FormattedMessage {...messages.editPoll} />
                        </Button>
                        <Button
                            onClick={handleClosePoll}
                            size="sm"
                            variant="outline"
                            state="negative"
                        >
                            <FormattedMessage {...messages.closePoll} />
                        </Button>
                        {request.responseCount > 0 &&
                            <Button
                                onClick={handleFinalize}
                                size="sm"
                            >
                                <FormattedMessage {...messages.finalizeNotify} />
                            </Button>
                        }
                    </>
                )}
            </div>
        </div>
    )
}

export default SchedulingPollDetailPage
